import gql from 'graphql-tag';
import { FRAGMENT_BOOKING_LINK } from './booking';

const FRAGMENT_BOOKING_INFO = gql`
  fragment fragmentQueryBookingInfo on Booking {
    id
    category
    sourceBookingId
    checkInTaskStarted
    countDown
    heldToTime
    editable
    totalPrice
    tenancyLengthOption
    isRolling
    isRolledOver
    sourceRollingBooking {
      id
    }
    rolloverBookings {
      id
    }
    bookingLink {
      ...fragmentBookingLink
    }
    academicYear {
      id
      name
      flexibilityControlInfo {
        allowStudentToEditAwayDates
      }
    }
    customFieldValues {
      id
      customFieldId
      customFieldType
      labelName
      value
      customFieldInputOnBookingJourney
      customFieldMandatoryOnBookingJourney
    }
    outOfRoomRecords {
      id
      bookingId
      reason
      awayFrom
      awayTo
    }
    vouchers {
      id
      name
      value
      partner {
        id
        name
      }
      description
      issued
      issuedDate
    }
    installment {
      installmentId
      name
    }
    serviceSelections {
      id
      serviceId
      serviceOptionId
      serviceName
      serviceType
      serviceOptionName
      incrementPerNight
    }
    specialRateOffered
    latestAmendmentDetail {
      targetBookingId
      status
      type
    }
    amendmentDetails {
      sourceBookingId
      targetBookingId
      status
      type
    }
    sourceAmendmentDetails {
      sourceBookingId
      targetBookingId
      status
      type
    }
    activeBookingsOnSameChain {
      id
      status
      checkInTaskStarted
      status
      countDown
      heldToTime
      tenancyOption {
        startDate
        endDate
        name
      }
      amendmentDetails {
        sourceBookingId
        targetBookingId
        type
        status
      }
    }
    sourceBooking {
      id
      status
      checkInTaskStarted
      tenancyOption {
        startDate
        endDate
        name
      }
    }
    targetBookings {
      id
      checkInTaskStarted
      status
      countDown
      heldToTime
      tenancyOption {
        startDate
        endDate
      }
    }
    bookingChainId
    bookingForms {
      id
      name
      type
      filePath
      status
    }
    student {
      account {
        id
      }
      contact {
        emergencyContacts {
          id
          contactEmail
          firstName
          middleName
          lastName
          title
          dateOfBirth
          mobilePhoneAreaCode
          mobilePhoneNumber
          homePhoneAreaCode
          homePhoneNumber
          addressLine1
          addressLine2
          postcode
          countryTerritory
          countryTerritoryId
          cityTown
          cityTownId
          relationship
          contactWithoutPermission
        }
      }
    }
    inspectionReports {
      id
      name
      inspectionTime
      inspectionReportItems {
        facilityId
        itemName
        itemCategory
        status
        proofPhotoUuids
        comment
        charge
      }
      editInfo {
        updatedBy
      }
    }
    roomAllocation {
      bedId
      bedName
      buildingName
      flatName
      floorName
    }
    roomType {
      roomTypeId
      name
    }
    tenancyOption {
      originalEndDate
      startDate
      endDate
      name
    }
    status
    property {
      id
      name
      imageName
      addressLine1
      addressLine2
      postalCode
      contactAreaCode
      contactEmailAddress
      contactPhoneNumber
      currency
      areaUnitOfMeasurement
      city {
        name
      }
      country {
        name
      }
      paymentSettings {
        paymentChannel
        paymentMethod
        isEnabled
      }
      timezone
      ownerName
    }
    contracts {
      id
      name
      fileUuid
      contractStage
      status
    }
    guarantors {
      id
      relationship
      firstName
      middleName
      lastName
      title
      dateOfBirth
      countryTerritory
      cityTown
      cityTownId
      countryTerritoryId
      taxCountryTerritory
      taxCountryTerritoryId
      postcode
      addressLine1
      addressLine2
      mobilePhoneAreaCode
      mobilePhoneNumber
      mobilePhone
      homePhoneAreaCode
      homePhoneNumber
      homePhone
      contactEmail
      customFieldValues {
        id
        customFieldId
        customFieldType
        labelName
        value
        customFieldInputOnBookingJourney
        customFieldMandatoryOnBookingJourney
      }
      documents {
        id
        documentSettingId
        name
        status
        stage
        fileUuid
        guarantorId
        category
        mandatory
        description
        rejectedReason
      }
      guarantorReferenceNumber
      guarantorType
    }
    transactionRecords(type: [TRANSFER_FROM, TRANSFER_TO]) {
      id
      type
      targetBookingId
    }
    paymentItems {
      id
      modelType
      category
      name
      description
      currency
      paidAmount
      totalAmount
      status
      isRefundable
      invoiceDatetime
      isDueBeforeBookingConfirmed
      chargeCreated
      dueDatetime
    }
    invoicedPaymentItems {
      id
      modelType
      category
      name
      description
      currency
      paidAmount
      totalAmount
      status
      isRefundable
      invoiceDatetime
      isDueBeforeBookingConfirmed
      chargeCreated
      dueDatetime
    }
    checkInDate
    checkInTaskStarted
    arrivalInformation {
      id
      arrivalDate
      timeSlotFrom
      timeSlotTo
      modeOfArrival
      flightNumber
      message
    }
    attachments {
      id
      name
      fileUuid
    }
    defaultCard {
      id
      type
      expireYear
      expireMonth
      cardBrand
      last4
      funding
      originId
      detail
      name
      email
    }
    backupCard {
      id
      type
      expireYear
      expireMonth
      cardBrand
      last4
      funding
      originId
      name
      email
    }
    documents {
      id
      name
      status
      stage
      fileUuid
      guarantorId
      category
      mandatory
      description
      rejectedReason
    }
    guarantorSettings {
      id
      academicYearId
      guarantorType
      guarantorTypeLabelName
      guarantorTypeDescription
    }
    currentGuarantorSetting {
      id
      academicYearId
      guarantorType
      guarantorTypeLabelName
      guarantorTypeDescription
    }
    bedStatus {
      id
      bed {
        id
        type {
          id
          name
        }
        room {
          id
          roomSize
          disabilityAccess
        }
      }
    }
  }
  ${FRAGMENT_BOOKING_LINK}
`;

const FRAGMENT_MY_PAYMENT_BOOKING_INFO = gql`
  fragment fragmentMyPaymentQueryBookingInfo on Booking {
    id
    bookingChainId
    checkInTaskStarted
    countDown
    heldToTime
    editable
    totalPrice
    totalPaidAmount
    tenancyLengthOption
    isRolling
    isRolledOver
    bookingLink {
      status
    }
    documents {
      id
      status
      stage
      mandatory
    }
    sourceRollingBooking {
      id
    }
    rolloverBookings {
      id
    }
    bookingAdditionalFees {
      name
      dueAmount
    }
    academicYear {
      id
      flexibilityControlInfo {
        studentTopUpWalletAfterCheckIn
        studentTopUpWalletBeforeCheckIn
        displaySelectedServiceDuringBookingJourney
      }
    }
    nominationPlan {
      id
      studentPayTo
      partner {
        name
        id
      }
    }
    specialRateOffered
    latestAmendmentDetail {
      targetBookingId
      status
      type
    }
    creditBalanceInfo {
      totalValidAmount
      creditBalances {
        remainingAmount
        category
      }
    }
    creditTransactionRecords {
      amount
      type
      category
      currency
      description
      editInfo {
        createdAt
      }
    }
    sourceAmendmentDetails {
      sourceBookingId
      targetBookingId
      status
      type
    }
    activeBookingsOnSameChain {
      id
      status
      countDown
      heldToTime
      amendmentDetails {
        type
        status
      }
    }
    targetBookings {
      id
      status
      countDown
      heldToTime
    }
    bookingForms {
      id
      name
      type
      filePath
      status
    }
    student {
      account {
        id
      }
      contact {
        emergencyContacts {
          id
          contactEmail
          firstName
          middleName
          lastName
          title
          dateOfBirth
          mobilePhoneAreaCode
          mobilePhoneNumber
          homePhoneAreaCode
          homePhoneNumber
          addressLine1
          addressLine2
          postcode
          countryTerritory
          countryTerritoryId
          cityTown
          cityTownId
          relationship
          contactWithoutPermission
        }
      }
    }
    balanceInfo {
      walletBalance
      bookingBalance
      currentBalance
      refundableBalance
      totalBalance
      financialStatus
      processingBalance
      amendmentCreditBalance
    }
    checkOutCartInfo {
      bookingTotalFeeDetail {
        rent {
          tenancyLength
          amountAfterTax
          originalAmount
          taxInfo {
            taxRate
            amount
            includedInPrice
          }
        }
        services {
          name
          amountAfterTax
          originalAmount
          taxInfo {
            taxRate
            amount
            includedInPrice
          }
        }
        additionalFees {
          name
          amountAfterTax
          originalAmount
          taxInfo {
            taxRate
            amount
            includedInPrice
          }
        }
        specialOffer {
          name
          amount
        }
        refundableFees {
          name
          amount
        }
        totalFee
      }
    }
    roomType {
      roomTypeId
      name
    }
    arrivalInformation {
      id
      arrivalDate
      timeSlotFrom
      modeOfArrival
      flightNumber
      message
    }
    roomAllocation {
      bedId
      bedName
      buildingName
      flatName
      floorName
    }
    tenancyOption {
      originalEndDate
      startDate
      endDate
      name
    }
    status
    property {
      id
      name
      imageName
      addressLine1
      addressLine2
      postalCode
      contactAreaCode
      contactEmailAddress
      contactPhoneNumber
      managementCompanyName
      currency
      timezone
      ownerName
      roomTypes {
        id
        originId
        name
        kitchenArrangement
        bathroomArrangement
        category
        hasPrice
      }
      paymentSettings {
        paymentChannel
        paymentMethod
        isEnabled
      }
      city {
        name
      }
      country {
        id
        name
        countryCode
      }
    }
    installmentPlanInfo {
      name
    }
    invoicedPaymentItems {
      id
      modelType
      category
      name
      description
      currency
      paidAmount
      totalAmount
      status
      isRefundable
      invoiceDatetime
      isDueBeforeBookingConfirmed
      chargeCreated
      dueDatetime
    }
    transactionRecords(type: [PAYMENT, TRANSFER_TO, TRANSFER_FROM, REVERSAL_PAYMENT, REFUND]) {
      id
      originId
      type
      amount
      category
      description
      channel
      method
      cardBrand
      receivedCurrency
      receivedAmount
      sourceBookingId
      targetBookingId
      actualTransactionDate
      isProcessing
      destinationDetails
      editInfo {
        createdAt
      }
    }
    paymentItems {
      id
      modelType
      category
      description
      name
      currency
      paidAmount
      paidAmountFromCredit
      paidAmountFromWallet
      totalAmount
      status
      isRefundable
      isPartOfRent
      invoiceDatetime
      isDueBeforeBookingConfirmed
      chargeCreated
      dueDatetime
      model {
        ... on BookingAdditionalFee {
          description
        }
      }
      transactionRecords {
        id
        originId
        type
        amount
        category
        description
        channel
        method
        cardBrand
        receivedCurrency
        receivedAmount
        editInfo {
          createdAt
        }
      }
    }
    defaultCard {
      id
      name
      email
      type
      expireYear
      expireMonth
      cardBrand
      last4
      funding
      originId
    }
    backupCard {
      id
      name
      email
      type
      expireYear
      expireMonth
      cardBrand
      last4
      funding
      originId
    }
  }
`;

export const QUERU_STUDENT_GET_BOOKING = gql`
  query SP_StudentGetBookingInfo($id: ID!) {
    studentGetBooking(id: $id) {
      ...fragmentQueryBookingInfo
    }
  }
  ${FRAGMENT_BOOKING_INFO}
`;

export const QUERUY_MY_PAYMENTS_STUDENT_GET_BOOKING = gql`
  query SP_MyPaymentGetBookingInfo($id: ID!) {
    studentGetBooking(id: $id) {
      ...fragmentMyPaymentQueryBookingInfo
    }
  }
  ${FRAGMENT_MY_PAYMENT_BOOKING_INFO}
`;

export const QUERUY_GET_AVAILABLE_TIME_SLOTS = gql`
  query SP_GetAvailableTimeSlots($propertyId: ID!, $targetDate: Date!) {
    getAvailableTimeSlots(propertyId: $propertyId, targetDate: $targetDate) {
      arrivalTimeSlots {
        timeSlotFrom
      }
      arrivalTimeSlotSetting {
        slotLength
      }
    }
  }
`;

export const MUTATION_CREATE_ARRIVAL_INFORMATION = gql`
  mutation SP_MutationCreateArrivalInformation($input: studentCreateArrivalInformationInput!) {
    studentCreateArrivalInformation(input: $input) {
      id
    }
  }
`;

export const MUTATION_UPDATE_ARRIVAL_INFORMATION = gql`
  mutation SP_MutationUpdateArrivalInformation($input: studentUpdateArrivalInformationInput!) {
    studentUpdateArrivalInformation(input: $input) {
      id
    }
  }
`;

export const MUTATION_STUDENT_UPDATE_EMERGENCY_CONTACT = gql`
  mutation SP_MutationStudentUpdateEmergencyContact($input: updateStudentEmergencyContactInput!) {
    studentUpdateEmergencyContact(input: $input) {
      id
    }
  }
`;

export const MUTATION_STUDENT_DELETE_EMERGENCY_CONTACT = gql`
  mutation SP_MutationStudentDeleteEmergencyContact($id: ID!) {
    studentDeleteEmergencyContact(id: $id) {
      deletedId
    }
  }
`;

export const MUTATION_STUDENT_CREATE_EMERGENCY_CONTACT = gql`
  mutation SP_MutationStudentCreateEmergencyContact($input: createStudentEmergencyContactInput!) {
    studentCreateEmergencyContact(input: $input) {
      id
    }
  }
`;

export const MUTATION_STUDENT_UPDATE_BOOKING_DOCUMENT = gql`
  mutation SP_MutationStudentUpdateBookingDocument(
    $propertyId: ID!
    $input: updateBookingDocumentInput!
  ) {
    studentUpdateBookingDocument(propertyId: $propertyId, input: $input) {
      id
    }
  }
`;

export const MUTATION_STUDENT_DELETE_BOOKING_DOCUMENT = gql`
  mutation SP_MutationStudentDeleteBookingDocument(
    $propertyId: ID!
    $input: deleteBookingDocumentInput!
  ) {
    studentDeleteBookingDocument(propertyId: $propertyId, input: $input) {
      id
    }
  }
`;

export const MUTATION_STUDENT_EXPIRE_BOOKING = gql`
  mutation SP_MutationStudentExpireBooking($bookingId: ID!, $reason: StudentExpireBookingReason!) {
    studentExpireBooking(bookingId: $bookingId, reason: $reason) {
      id
    }
  }
`;

export const MUTATION_STUDENT_REJECT_TAKEOVER = gql`
  mutation SP_MutationStudentRejectTakeover(
    $bookingId: ID!
    $reason: StudentRejectTakeoverReason!
  ) {
    studentRejectTakeover(bookingId: $bookingId, reason: $reason) {
      id
    }
  }
`;

export const MUTATION_UPDATE_BOOKING_FORM = gql`
  mutation SP_MutationUpdateBookingForm(
    $propertyId: ID!
    $bookingId: ID!
    $id: ID!
    $status: BookingFormStatusOption!
  ) {
    updateBookingForm(propertyId: $propertyId, bookingId: $bookingId, id: $id, status: $status) {
      id
      status
    }
  }
`;

export const MUTATION_CREATE_OUT_OF_ROOM_RECORD = gql`
  mutation SP_MutationCreateOutOfRoomRecord($propertyId: ID!, $input: createOutOfRoomRecordInput!) {
    createOutOfRoomRecord(propertyId: $propertyId, input: $input) {
      id
    }
  }
`;

export const MUTATION_UPDATE_OUT_OF_ROOM_RECORD = gql`
  mutation SP_MutationUpdateOutOfRoomRecord($propertyId: ID!, $input: updateOutOfRoomRecordInput!) {
    updateOutOfRoomRecord(propertyId: $propertyId, input: $input) {
      id
    }
  }
`;

export const MUTATION_DELETE_OUT_OF_ROOM_RECORD = gql`
  mutation SP_MutationDeleteOutOfRoomRecord($propertyId: ID!, $id: ID!) {
    deleteOutOfRoomRecord(propertyId: $propertyId, id: $id) {
      success
    }
  }
`;

export const MUTATION_STUDENT_UPSER_BOOKING_CUSTOM_FIELD = gql`
  mutation SP_MutationStudentUpsertBookingCustomFieldValues(
    $input: upsertBookingCustomFieldValuesInput!
  ) {
    studentUpsertBookingCustomFieldValues(input: $input) {
      customFieldValues {
        id
      }
    }
  }
`;

export const QUERUY_STUDENT_GET_REFUND_DETAILS_REFERENCE_INFOS = gql`
  query SP_StudentGetRefundDetailsInfos(
    $propertyId: ID!
    $filter: getRefundDetailReferenceInfosInput
  ) {
    studentGetRefundDetailReferenceInfos(propertyId: $propertyId, filter: $filter) {
      totalRefund
      processingRefund
      refundDetailReferenceInfos {
        id
        originId
        destinationDetails
        editInfo {
          createdAt
        }
        refundDetail {
          id
          originId
          propertyId
          amount
          accountNumber
          approvalProcessTask {
            id
            approvalTemplate {
              id
              customizedData {
                bankAccountCountryType
                bankAccountCountryList {
                  id
                  name
                }
              }
            }
          }
        }
        amount
        status
        channel
        payment {
          id
          originId
          method
          description
        }
        failureReason
      }
    }
  }
`;

export const QUERUY_OFFLINE_REFUND_BANK_ACCOUNT_CONFIG = gql`
  query SP_GetOfflineRefundBankAccountConfig(
    $propertyId: ID!
    $input: getOfflineRefundBankAccountConfigInput!
  ) {
    getOfflineRefundBankAccountConfig(propertyId: $propertyId, input: $input) {
      offlineBankAccountConfigs {
        name
      }
    }
  }
`;

export const MUTATION_STUDENT_UPDATE_REFUND_DETAILS = gql`
  mutation SP_MutationStudentUpdateRefundDetails(
    $propertyId: ID!
    $input: updateRefundDetailsInput!
  ) {
    studentUpdateRefundDetails(propertyId: $propertyId, input: $input) {
      id
    }
  }
`;

export const QUERY_IBAN_VALIDATE_DATA = gql`
  query SP_QueryIBANValidateData($propertyId: ID!, $input: getIBANValidatedDataInput!) {
    getIBANValidatedData(propertyId: $propertyId, input: $input) {
      validatedBankAccountData {
        name
        value
      }
    }
  }
`;

export const QUERY_GET_PAYMENT_ITEM_BREAK_DOWN = gql`
  query SP_QueryGetPaymentItemBreakDown($propertyId: ID!, $input: getPaymentItemBreakDownInput!) {
    getPaymentItemBreakDown(propertyId: $propertyId, input: $input) {
      breakdown {
        rental {
          amountAfterTax
          isTaxIncludedInPrice
          name
          originalAmount
          taxAmount
          taxName
          taxRate
        }
        services {
          amountAfterTax
          isTaxIncludedInPrice
          name
          originalAmount
          taxAmount
          taxName
          taxRate
        }
        nonRental {
          amountAfterTax
          isTaxIncludedInPrice
          name
          originalAmount
          taxAmount
          taxName
          taxRate
        }
      }
    }
  }
`;
